import { Animated, Easing, LayoutRectangle, Platform, ScrollView, View } from 'react-native';
import React, { useCallback, useEffect, useRef } from 'react';
import Divider from '../Divider';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import { colors } from '../../../utils/theme';
import { getSpacing } from '../../../utils/spacing';
import { useForceUpdate } from '../../../hooks/useForceUpdate';
import MaxWidthRestrictedContent from '../MaxWidthRestrictedContent';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import TabLabel from './TabLabel';
import { BaseTabViewProps } from './utils';
import TabContentContainer from './TabContentContainer';
import { ANIMATION_DURATION } from './constants';
import { useCurrentAndPreviousTab } from './hooks';
import { getTabButtonTestId } from './utils';
import { PRScrollView } from '../../shared/Scroll';

export type TabViewOwnProps = BaseTabViewProps;
export type TabViewProps = TabViewOwnProps;

const getLeftPosition = (layout: LayoutRectangle, tabWidth: number) => layout.x + layout.width / 2 - tabWidth / 2;

const TabView: React.FC<TabViewProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const exitTransitionCompletedRef = useRef(true);
    const animationRunIdRef = useRef(0);
    const scrollRef = useRef<ScrollView | null>(null);
    const { forceUpdate } = useForceUpdate();
    const [layoutMap, setLayoutMap] = React.useState<Record<string, LayoutRectangle>>({});
    const [tabTextWidthMap, setTabTextWidthMap] = React.useState<{ [key: string]: number }>({});
    const tabLeftAnimationRef = useRef<null | Animated.Value>(null);
    const focusedTabLayout = layoutMap[props.selectedTab];
    const focusedTabTextWidth = tabTextWidthMap[props.selectedTab];
    useCurrentAndPreviousTab({
        selectedTab: props.selectedTab,
        onTabChange: () => {
            exitTransitionCompletedRef.current = false;
        },
    });
    useEffect(() => {
        if (focusedTabLayout && focusedTabTextWidth && !tabLeftAnimationRef.current) {
            tabLeftAnimationRef.current = new Animated.Value(getLeftPosition(focusedTabLayout, focusedTabTextWidth));
        }
    }, [focusedTabLayout, focusedTabTextWidth]);

    useEffect(() => {
        if (focusedTabLayout && tabLeftAnimationRef.current) {
            const runId = ++animationRunIdRef.current;
            exitTransitionCompletedRef.current = false;
            Animated.sequence([
                Animated.parallel([
                    Animated.timing(tabLeftAnimationRef.current, {
                        toValue: getLeftPosition(focusedTabLayout, focusedTabTextWidth || 0),
                        duration: ANIMATION_DURATION,
                        useNativeDriver: Platform.OS === 'web',
                        easing: Easing.inOut(Easing.ease),
                    }),
                ]),
            ]).start(() => {
                // Interrupted runs report finished: false; skipping them here would leave this
                // subtree tagged `animating-parent` for good. The run id keeps a superseded run from
                // clearing the flag its replacement still needs.
                if (animationRunIdRef.current !== runId) return;
                exitTransitionCompletedRef.current = true;
                forceUpdate();
            });
            forceUpdate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedTabLayout?.x]);

    const isDisabledByParent = useIsDisabledByParent();

    const handleLabelWidthChange = useCallback((width: number, tabValue: string) => {
        setTabTextWidthMap((prev) => ({ ...prev, [tabValue]: width }));
    }, []);

    return (
        <View
            style={{ width: '100%', flex: 1, position: 'relative', overflow: 'hidden' }}
            onLayout={(e) => {
                const layout = e.nativeEvent.layout;
                setLayoutMap((prev) => ({ ...prev, container: layout }));
            }}
            testID={exitTransitionCompletedRef?.current ? undefined : 'animating-parent'}
        >
            <MaxWidthRestrictedContent variant={props.restrictedWidthVariant}>
                <View style={{ width: '100%', overflow: 'visible' }}>
                    <PRScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={{
                            backgroundColor: colors.White,
                            minWidth: '100%',
                            display: 'flex',
                            position: 'relative',
                        }}
                        contentContainerStyle={{ minWidth: '100%', flexGrow: 1 }}
                        ref={scrollRef}
                    >
                        {props.tabs
                            .filter((_) => !_.hide)
                            .map((tab) => {
                                const isDisabled = isDisabledByParent || tab.isDisabled || !layoutMap[tab.value];
                                return (
                                    <View
                                        key={tab.value}
                                        style={{
                                            flexGrow: 1,
                                            height: isMobile ? 60 : 45,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'flex-end',
                                            overflow: 'visible',
                                            paddingLeft: getSpacing(8),
                                            paddingRight: getSpacing(8),
                                        }}
                                        onLayout={(e) => {
                                            const layout = e.nativeEvent.layout;
                                            /*
                                        if width is going to 0, the screen is navigated away from.
                                        Don't set layout to this because it will cause useless animations when navigating back
                                    */
                                            if (layout.width === 0) return;

                                            setLayoutMap((prev) => ({
                                                ...prev,
                                                [tab.value]: layout,
                                            }));
                                        }}
                                    >
                                        <PressableDynamicOpacity
                                            isDisabled={isDisabled}
                                            onPress={() => {
                                                props.onChange(tab.value);
                                                // center the tab in the scroll view
                                                const tabCenterPoint =
                                                    layoutMap[tab.value].x + layoutMap[tab.value].width / 2;
                                                const positionOfTabAtLeft =
                                                    layoutMap.container.x +
                                                    tabCenterPoint -
                                                    layoutMap.container.width / 2;
                                                scrollRef.current?.scrollTo({
                                                    x: positionOfTabAtLeft,
                                                    animated: true,
                                                });
                                            }}
                                            testID={getTabButtonTestId(tab.value, !!tab.isDisabled)}
                                        >
                                            <View
                                                style={{
                                                    justifyContent: 'flex-end',
                                                    alignItems: 'center',
                                                    paddingBottom: getSpacing(4),
                                                    height: '100%',
                                                }}
                                            >
                                                <TabLabel
                                                    tabValue={tab.value}
                                                    onWidthChange={handleLabelWidthChange}
                                                    label={tab.title}
                                                    isSelected={props.selectedTab === tab.value}
                                                    width={tabTextWidthMap[tab.value] || 0}
                                                />
                                            </View>
                                        </PressableDynamicOpacity>
                                    </View>
                                );
                            })}
                        <Animated.View
                            style={{
                                height: 5,
                                alignItems: 'center',
                                transform: [{ translateY: 2.75 }],
                                overflow: 'visible',
                                width: tabTextWidthMap[props.selectedTab] || 0,
                                backgroundColor: colors['Primary-Reg'],
                                position: 'absolute',
                                bottom: 0,
                                left:
                                    exitTransitionCompletedRef.current &&
                                    focusedTabLayout &&
                                    typeof tabTextWidthMap[props.selectedTab] === 'number'
                                        ? getLeftPosition(focusedTabLayout, tabTextWidthMap[props.selectedTab])
                                        : tabLeftAnimationRef.current
                                        ? tabLeftAnimationRef.current
                                        : 0,
                            }}
                        />
                    </PRScrollView>
                    <Divider horizontal style={{ zIndex: -1 }} />
                </View>
            </MaxWidthRestrictedContent>
            <TabContentContainer
                tabs={props.tabs}
                selectedTab={props.selectedTab}
                scrollable={!!props.scrollable}
                tabContainerWidth={layoutMap.container?.width || 0}
                onlyRenderActiveTab={props.onlyRenderActiveTab}
            />
        </View>
    );
};

export default React.memo(TabView);
